import { ScrollHandler } from '@repo/utils';
import { useScrollIntent } from 'providers/ScrollIntentProvider';
import { useEffect, useLayoutEffect, useRef, type RefObject } from 'react';
import { useLocation } from 'react-router-dom';
import { restoreWhenReachable } from 'utils/scroll/restoreWhenReachable';

const scrollHandler = ScrollHandler.instance;

/**
 * Remembers the scroll position of every pathname, and carries out the
 * navigation's scroll intent when the pathname changes. Hash intents are left
 * to useHashScroll.
 */
export const useApplyScrollIntent = (containerRef: RefObject<HTMLElement>) => {
  const { pathname } = useLocation();
  const intent = useScrollIntent();
  const pathnameRef = useRef(pathname);

  useLayoutEffect(() => {
    pathnameRef.current = pathname;
    const container = containerRef.current;
    if (!container) return;

    if (intent.kind === 'top') {
      container.scrollTo({ top: 0 });
      // A page already at the top fires no scroll event to overwrite a stale
      // position, which a later back would restore
      scrollHandler.saveScrollPosition(pathname, 0);
    }
    if (intent.kind === 'restore') {
      return restoreWhenReachable(
        container,
        scrollHandler.getScrollPosition(pathname),
      );
    }
    // Search and state changes keep the page where it is
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, containerRef]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let frame = 0;
    const save = () => {
      // Read now: by the next frame the page may already have changed
      const path = pathnameRef.current;
      const top = container.scrollTop;
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() =>
        scrollHandler.saveScrollPosition(path, top),
      );
    };

    container.addEventListener('scroll', save, { passive: true });
    return () => {
      cancelAnimationFrame(frame);
      container.removeEventListener('scroll', save);
    };
  }, [containerRef]);
};
